import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GamePlayer } from '@/types/golf';
import { GolfAvatar } from '../ui/GolfAvatar';
import TeeBadge from '../game-setup/TeeBadge';

interface PlayerItemProps {
  player: GamePlayer;
  isCurrentUser: boolean;
  onClick: () => void;
  onRemove?: () => void;
}

/**
 * PlayerItem component for displaying a player in the player list
 * Used in GameSetupPlayers and PlayerComponent
 */
const PlayerItem: React.FC<PlayerItemProps> = ({ player, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: player.userId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 1,
    touchAction: 'none' as const,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex flex-col items-center relative ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      onClick={() => {
        if (!isDragging) {
          onClick();
        }
      }}
    >
      {/* Player Avatar */}
      <div className="relative mb-2">
        <GolfAvatar
          name={player.name}
          imageUrl={player.imageUrl}
          className='aspect-square size-full rounded-lg'
        />
      </div>

      {/* Player Name */}
      <div className="text-center">
        <div className="font-medium text-sm">{player.name.split(' ')[0]}</div>
      </div>

      {/* Player Handicap */}
      <div className="text-xs text-gray-500 mt-1">
        HCP {typeof player.handicap === 'string' ? parseInt(String(player.handicap)) : (player.handicap || 54)}
      </div>

      {/* Tee Badge */}
      <div className="mt-1 flex justify-center">
        <TeeBadge teeName={player.teeName} defaultTee={player.teeName} size="sm" />
      </div>
    </div>
  );
};

export default PlayerItem;
